import React from "react";
import BrandMark from "./BrandMark";
import PreviewValue from "./PreviewValue";
import mealPlanBg from "../../../assets/svg/my brand/meal plan-bg.svg";
import { mealPlanSample, previewSample } from "../data/brandMock";
import { BrandDetails } from "../types/brand.types";
import { senderName } from "../previewFields";

/** Column in the strip under the client's name. */
const PlanFact: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="min-w-0">
    <div className="text-[7px] text-ink-subtle">{label}</div>
    <div className="mt-0.5 text-[9px] text-[#111C2D] truncate">{value}</div>
  </div>
);

interface MealPlanPreviewProps {
  details: BrandDetails;
}

/**
 * Cover page of a generated meal plan. The dietitian's branding sits at the
 * top; the client, plan id and dates are sample data standing in for whatever
 * plan the document is generated from.
 */
const MealPlanPreview: React.FC<MealPlanPreviewProps> = ({ details }) => (
  <div className="relative h-full bg-white">
    {/* The plan template's artwork, full bleed behind the content. `cover`
        rather than a stretch: the page and the artboard are within a couple
        of percent of each other, and skewing the geometry shows. */}
    <img
      src={mealPlanBg}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full object-cover"
    />

    <div className="relative flex h-full flex-col justify-center px-14">
      {/* Same box as the invoice's — the brand's logo reads as one
          consistent size across every document, not rescaled per page. */}
      <BrandMark
        details={details}
        size={40}
        width={100}
        squareSize={{ width: 52, height: 52 }}
      />

      <h3 className="mt-8 text-[25px] font-semibold leading-tight text-[#111C2D]">
        {mealPlanSample.clientName}
      </h3>
      <div className="mt-1.5 text-[8px] text-ink-subtle">
        Plan ID :{" "}
        <span className="font-semibold text-[#00899B]">
          {mealPlanSample.planId}
        </span>
      </div>

      {/* "Prepared by" holds the longest value of the three, so it takes the
          wider track rather than an equal third. */}
      <div className="mt-6 grid grid-cols-[1fr_1fr_2fr] gap-4 rounded-[4px] bg-[#F5F7F8] px-2.5 py-2">
        <PlanFact label="Start date" value={mealPlanSample.startDate} />
        <PlanFact label="Goal" value={mealPlanSample.goal} />
        <PlanFact
          label="Prepared by"
          value={
            <PreviewValue
              value={senderName(details)}
              sample={previewSample.fullName}
              className="text-[#111C2D]"
            />
          }
        />
      </div>
    </div>
  </div>
);

export default MealPlanPreview;
